<template>
  <div
    v-if="file"
    class="attachment"
  >
    <div class="attachment__info">
      <span v-html="fileStatus" />
      <div
        ref="name"
        :contenteditable="editing"
        :class="{'attachment__name': true, 'attachment__editing': editing}"
        @keypress="checkKeyInput"
        @input="updateName"
        v-html="file.data.original_name"
      />
      <span
        v-if="file.isEditable()"
        class="uk-icon-pencil"
        @click="setEditing"
      />
      <span
        v-if="editing"
        class="uk-button uk-button-small uk-button-primary"
        @click="finishEdit"
      ><span
        class="uk-icon-save"
      /></span>
      <div class="attachment__info__end">
        <span v-if="file.isRetrying()">retrying upload...</span>
        <span
          v-if="file.isEditable()"
          class="uk-icon-trash uk-text-danger"
          @click="deleteFile"
        />
      </div>
    </div>
    <div
      v-if="file.errors.message"
      class="uk-text-danger"
    >
      {{ file.errors.message }}
    </div>

    <div
      :style="{visibility: !file.isInProgress() ? 'hidden' : 'visible'}"
      class="attachment__upload"
    >
      <div :style="{width: file.progress + '%'}" />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import UploadedFileModel, {create} from "../../models/UploadedFile";
import { getUploader } from "../../lib/upload";

@Component
export default class Attachment extends Vue {
  @Prop({required: true}) reference!: string;

  @Prop({default: "Are you sure you want to delete this file?"}) deleteConfirmation!: string;

  editing = false;

  draftName = '';

  isFirstAttempt = true;

  get retrying(): boolean {
    const retrying = this.file.isRetrying();
    if (this.isFirstAttempt && retrying) {
      this.isFirstAttempt = false;
      this.$emit('retrying');
    }

    return retrying;
  }

  get file(): UploadedFileModel {
    return this.$store.getters['files/fileByRef'](this.reference)
  }

  get fileStatus(): string {
    if (this.file.progress === 100 || this.file.data.is_complete) {
      return '<span class="uk-icon-check attachment__status"></span>';
    }
    if (this.file.progress === -1) {
      return '<span class="uk-icon-exclamation-triangle uk-text-danger attachment__status"></span>';
    }

    return '<span class="attachment__status">' + this.file.progress + '%</span>';
  }

  deleteFile(): void {
    if (confirm(this.deleteConfirmation)) {
      if (this.file.isInProgress()) {
        getUploader('entries').cancelCurrentFile();
      }

      // Fail silently and still send the deleted event so it can be removed from parent.
      this.$store.dispatch('files/deleteFile', this.reference).catch(() => false);
      this.$emit('deleted', this.reference);
    }
  }

  setEditing(): void {
    this.editing = !this.editing;
  }

  updateName(e: Event): void {
    this.draftName = (e.target as HTMLElement).innerHTML;
  }

  finishEdit(): void {
    this.editing = false;

    const file = create({
      reference: this.reference,
      original_name: this.draftName
    });

    if (this.draftName.length > 0) {
      this.$store.dispatch('files/update', file).then(() => this.draftName = '');
    }
  }

  checkKeyInput(e: KeyboardEvent): void {
    if (e.keyCode === 13) {
      this.editing = false;
      this.finishEdit();
    }

    const char = String.fromCharCode(e.keyCode);
    if (!/[A-Za-z0-9\s-_.()\[\]]/.test(char)) {
      e.preventDefault();
    }
  }

}
</script>

<style scoped>

.attachment {
  min-height: 1.5em;
}

</style>
